import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Stack,
  CircularProgress,
  Typography,
  Tooltip,
} from "@mui/material";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { useSnackbar } from "notistack";
import Content from "../../../components/Content";
import AddressSearchField from "../../../components/settings/AddressSearchField";
import GeofenceDrawMap from "../../../components/settings/GeofenceDrawMap";
import { useGeofence, useUpsertGeofence } from "../../../hooks/useGeofence";
import "../../../styles/settings/geofence.css";

const getOrgId = () => {
  try {
    const userData = localStorage.getItem("userData");
    if (!userData) return null;
    return JSON.parse(userData)?.organisation_id ?? null;
  } catch {
    return null;
  }
};

const Step = ({ index, state, title, hint }) => (
  <div className={`geofence-step ${state}`}>
    <div className="geofence-step-indicator">
      {state === "done" ? "✓" : index}
    </div>
    <div className="geofence-step-body">
      <div className="geofence-step-title">{title}</div>
      <div className="geofence-step-hint">{hint}</div>
    </div>
  </div>
);

export const GeofenceSettingsView = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [organisationId] = useState(getOrgId);
  const { data: saved, isLoading } = useGeofence(organisationId);
  const upsert = useUpsertGeofence();

  const [address, setAddress] = useState("");
  const [center, setCenter] = useState(null);
  const [polygon, setPolygon] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!saved) return;
    setAddress(saved.address ?? "");
    if (saved.center_lat != null && saved.center_lng != null) {
      setCenter({ lat: saved.center_lat, lng: saved.center_lng });
    }
    setPolygon(saved.polygon ?? null);
    setDirty(false);
  }, [saved]);

  const hasPolygon = Boolean(polygon?.coordinates?.[0]?.length >= 4);

  const stepState = useMemo(() => {
    const addressDone = Boolean(center);
    const polygonDone = hasPolygon;
    return {
      address: addressDone ? "done" : "active",
      draw: !addressDone ? "pending" : polygonDone ? "done" : "active",
      save: !polygonDone ? "pending" : "active",
    };
  }, [center, hasPolygon]);

  const handleSelectAddress = (opt) => {
    setAddress(opt.label);
    setCenter({ lat: opt.lat, lng: opt.lng });
    setDirty(true);
  };

  const handlePolygonChange = (next) => {
    setPolygon(next);
    setDirty(true);
  };

  const handleSave = async () => {
    if (!organisationId) {
      enqueueSnackbar("Saknar organisation_id — logga in på nytt.", {
        variant: "error",
      });
      return;
    }
    try {
      await upsert.mutateAsync({
        organisationId,
        address: address || null,
        center,
        polygon,
      });
      enqueueSnackbar("Platsområde sparat", { variant: "success" });
      setDirty(false);
    } catch (err) {
      console.error(err);
      enqueueSnackbar(
        err?.message ? `Kunde inte spara: ${err.message}` : "Kunde inte spara",
        { variant: "error" }
      );
    }
  };

  return (
    <Content
      title="Platsområde"
      subtitle="Definiera vilket område som räknas till er adress"
    >
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          display: "flex",
          gap: { xs: 2, md: 3 },
          flexDirection: { xs: "column", lg: "row" },
          alignItems: "stretch",
        }}
      >
        {/* LEFT PANEL */}
        <Box
          sx={{
            flex: "0 0 auto",
            width: { xs: "100%", lg: 400 },
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <div className="geofence-control-card">
            <div className="geofence-eyebrow">Inställning</div>
            <div className="geofence-display-title">Rita er plats</div>
            <Typography
              variant="body2"
              sx={{
                color: "#5b6674",
                mt: 1,
                lineHeight: 1.55,
                fontSize: 14,
              }}
            >
              När ett larm utlöses jämförs varje användares position mot det
              här området — så ni direkt ser vilka som var på plats.
            </Typography>

            <Box sx={{ mt: 3 }}>
              <div className="geofence-stepper">
                <Step
                  index={1}
                  state={stepState.address}
                  title="Sök er adress"
                  hint="Gata, postort eller en välkänd plats. Välj sedan rätt träff i listan."
                />
                <Step
                  index={2}
                  state={stepState.draw}
                  title="Rita området på kartan"
                  hint="Tryck på Starta ritning i kartan. Klicka ut hörnen runt platsen."
                />
                <Step
                  index={3}
                  state={stepState.save}
                  title="Spara"
                  hint="Klart. Ni kan ändra området när som helst."
                />
              </div>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "#8a94a4",
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  fontSize: 10.5,
                }}
              >
                Adress
              </Typography>
              <Box sx={{ mt: 1 }}>
                <AddressSearchField
                  value={address}
                  onSelect={handleSelectAddress}
                />
              </Box>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Tooltip
                title={
                  !hasPolygon
                    ? "Rita först ett område på kartan"
                    : !dirty
                    ? "Inga ändringar att spara"
                    : ""
                }
                placement="top"
              >
                <span>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    startIcon={
                      upsert.isLoading ? (
                        <CircularProgress size={16} sx={{ color: "#ffffff" }} />
                      ) : (
                        <SaveRoundedIcon />
                      )
                    }
                    disabled={
                      !hasPolygon || upsert.isLoading || isLoading || !dirty
                    }
                    onClick={handleSave}
                    sx={{
                      py: 1.25,
                      fontSize: 14,
                      fontWeight: 700,
                      textTransform: "none",
                      borderRadius: 2.5,
                      boxShadow: "0 6px 18px -6px rgba(138,17,37,0.55)",
                    }}
                  >
                    {saved ? "Uppdatera område" : "Spara område"}
                  </Button>
                </span>
              </Tooltip>
            </Box>

            {saved?.updated_at ? (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 2,
                  color: "#9aa1ab",
                  fontSize: 11.5,
                }}
              >
                Senast sparat {new Date(saved.updated_at).toLocaleString("sv-SE")}
              </Typography>
            ) : null}
          </div>

          {/* Helper card */}
          <Box
            sx={{
              p: 2.5,
              borderRadius: 3,
              background:
                "linear-gradient(135deg, rgba(16,50,74,0.04), rgba(138,17,37,0.05))",
              border: "1px dashed rgba(16,50,74,0.14)",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#8a1125",
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                fontSize: 10.5,
              }}
            >
              Tips
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#10324a", mt: 0.5, lineHeight: 1.5, fontSize: 13 }}
            >
              Håll området lite större än byggnaden — GPS-noggrannheten kan
              variera några meter. Dubbelklicka eller tryck "Avsluta ritning"
              när du är nöjd.
            </Typography>
          </Box>
        </Box>

        {/* RIGHT: MAP */}
        <Stack
          sx={{
            flex: 1,
            minHeight: { xs: 480, md: 640 },
          }}
        >
          <GeofenceDrawMap
            center={center}
            polygon={polygon}
            onChange={handlePolygonChange}
          />
        </Stack>
      </Box>
    </Content>
  );
};
